import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import { confirmAlert, errorAlert, successAlert, warningAlert } from "../../utils/alert";

const emptyForm = {
  id: null,
  sport_id: "",
  game_id: "",
  game_type: "",
  sets_count: 3,
  points_per_set: 0,
  deciding_set_points: "",
  win_by_two: true,
  segment_label: "Set",
  source_name: "",
  source_url: "",
  priority: 100,
  is_active: true,
};

const ScoreSheetRules = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [sports, setSports] = useState([]);
  const [games, setGames] = useState([]);
  const [gameTypes, setGameTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const loadData = async () => {
    setLoading(true);
    try {
      const [rulesRes, optsRes] = await Promise.all([
        API.fetchScoreSheetRules(),
        API.fetchScoreSheetRuleOptions(),
      ]);
      setRules(Array.isArray(rulesRes?.data) ? rulesRes.data : []);
      setSports(Array.isArray(optsRes?.data?.sports) ? optsRes.data.sports : []);
      setGames(Array.isArray(optsRes?.data?.games) ? optsRes.data.games : []);
      setGameTypes(Array.isArray(optsRes?.data?.game_types) ? optsRes.data.game_types : []);
    } catch (err) {
      errorAlert("Load failed", err?.message || "Could not load score sheet rules.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const sportById = useMemo(() => {
    const map = {};
    sports.forEach((s) => {
      map[String(s.id)] = s.name || `Sport #${s.id}`;
    });
    return map;
  }, [sports]);

  const filteredGames = useMemo(() => {
    const sid = Number(form.sport_id || 0);
    if (!sid) return games;
    return games.filter((g) => Number(g.sport_id || 0) === sid);
  }, [games, form.sport_id]);

  const visibleRules = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    return rules.filter((row) => {
      if (statusFilter === "active" && !row.is_active) return false;
      if (statusFilter === "inactive" && row.is_active) return false;
      if (!q) return true;
      const hay = `${row.sport_name || ""} ${row.game_name || ""} ${row.game_type || ""} ${row.segment_label || ""} ${row.source_name || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rules, search, statusFilter]);

  const onFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setForm(emptyForm);
  };

  const onSportChange = (value) => {
    const sid = String(value || "");
    setForm((prev) => ({
      ...prev,
      sport_id: sid,
      game_id: prev.game_id && !games.some((g) => String(g.id) === String(prev.game_id) && String(g.sport_id) === sid)
        ? ""
        : prev.game_id,
    }));
  };

  const validateForm = () => {
    const setsCount = Number(form.sets_count);
    const points = Number(form.points_per_set);
    const priority = Number(form.priority);
    if (!Number.isFinite(setsCount) || setsCount < 1 || setsCount > 9) {
      warningAlert("Validation", "Sets count must be between 1 and 9.");
      return false;
    }
    if (!Number.isFinite(points) || points < 0) {
      warningAlert("Validation", "Points per set must be 0 or greater.");
      return false;
    }
    if (!Number.isFinite(priority) || priority < 1) {
      warningAlert("Validation", "Priority must be 1 or greater.");
      return false;
    }
    if (!String(form.segment_label || "").trim()) {
      warningAlert("Validation", "Segment label is required.");
      return false;
    }
    return true;
  };

  const buildPayload = () => {
    const decidingRaw = String(form.deciding_set_points ?? "").trim();
    return {
      sport_id: form.sport_id ? Number(form.sport_id) : null,
      game_id: form.game_id ? Number(form.game_id) : null,
      game_type: String(form.game_type || "").trim() || null,
      sets_count: Number(form.sets_count || 0),
      points_per_set: Number(form.points_per_set || 0),
      deciding_set_points: decidingRaw === "" ? null : Number(decidingRaw),
      win_by_two: Boolean(form.win_by_two),
      segment_label: String(form.segment_label || "").trim(),
      source_name: String(form.source_name || "").trim() || null,
      source_url: String(form.source_url || "").trim() || null,
      priority: Number(form.priority || 100),
      is_active: Boolean(form.is_active),
    };
  };

  const saveRule = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      if (form.id) {
        await API.updateScoreSheetRule(form.id, payload);
        successAlert("Updated", "Score sheet rule updated.");
      } else {
        await API.createScoreSheetRule(payload);
        successAlert("Created", "Score sheet rule created.");
      }
      resetForm();
      await loadData();
    } catch (err) {
      errorAlert("Save failed", err?.message || "Could not save score sheet rule.");
    } finally {
      setSaving(false);
    }
  };

  const editRule = (row) => {
    setForm({
      id: row.id,
      sport_id: row.sport_id ? String(row.sport_id) : "",
      game_id: row.game_id ? String(row.game_id) : "",
      game_type: String(row.game_type || ""),
      sets_count: Number(row.sets_count || 3),
      points_per_set: Number(row.points_per_set || 0),
      deciding_set_points: row.deciding_set_points ?? "",
      win_by_two: Boolean(row.win_by_two),
      segment_label: String(row.segment_label || "Set"),
      source_name: String(row.source_name || ""),
      source_url: String(row.source_url || ""),
      priority: Number(row.priority || 100),
      is_active: Boolean(row.is_active),
    });
  };

  const deactivateRule = async (row) => {
    const confirm = await confirmAlert({
      title: "Deactivate rule?",
      text: "This will mark the rule inactive.",
      confirmText: "Deactivate",
      cancelText: "Cancel",
    });
    if (!confirm?.isConfirmed) return;
    try {
      await API.deleteScoreSheetRule(row.id);
      successAlert("Done", "Rule deactivated.");
      if (Number(form.id) === Number(row.id)) resetForm();
      await loadData();
    } catch (err) {
      errorAlert("Action failed", err?.message || "Could not deactivate rule.");
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto p-6">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Score Sheet Rules</h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage score sheet configuration per sport, game, and game type.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate("/admin")}
                className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-sm"
              >
                Back to Dashboard
              </button>
              <button
                onClick={resetForm}
                className="px-4 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-sm"
              >
                Clear Form
              </button>
              <button
                onClick={saveRule}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:bg-gray-400"
              >
                {saving ? "Saving..." : form.id ? "Update Rule" : "Add Rule"}
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-4 mb-4">
            <div className="grid md:grid-cols-4 gap-3">
              <label className="text-sm">
                Sport
                <select
                  value={form.sport_id}
                  onChange={(e) => onSportChange(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">All / Any</option>
                  {sports.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-sm">
                Game
                <select
                  value={form.game_id}
                  onChange={(e) => onFormChange("game_id", e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">All / Any</option>
                  {filteredGames.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.game_name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-sm">
                Game Type
                <select
                  value={form.game_type}
                  onChange={(e) => onFormChange("game_type", e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Any</option>
                  {gameTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-sm">
                Segment Label
                <input
                  value={form.segment_label}
                  onChange={(e) => onFormChange("segment_label", e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </label>

              <label className="text-sm">
                Sets Count
                <input
                  type="number"
                  min="1"
                  max="9"
                  value={form.sets_count}
                  onChange={(e) => onFormChange("sets_count", e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </label>

              <label className="text-sm">
                Points Per Set
                <input
                  type="number"
                  min="0"
                  value={form.points_per_set}
                  onChange={(e) => onFormChange("points_per_set", e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </label>

              <label className="text-sm">
                Deciding Set Points
                <input
                  type="number"
                  min="1"
                  value={form.deciding_set_points}
                  onChange={(e) => onFormChange("deciding_set_points", e.target.value)}
                  placeholder="Optional"
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </label>

              <label className="text-sm">
                Priority
                <input
                  type="number"
                  min="1"
                  value={form.priority}
                  onChange={(e) => onFormChange("priority", e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </label>

              <label className="text-sm md:col-span-2">
                Source Name
                <input
                  value={form.source_name}
                  onChange={(e) => onFormChange("source_name", e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </label>

              <label className="text-sm md:col-span-2">
                Source URL
                <input
                  value={form.source_url}
                  onChange={(e) => onFormChange("source_url", e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </label>

              <label className="text-sm inline-flex items-center gap-2 mt-6">
                <input
                  type="checkbox"
                  checked={form.win_by_two}
                  onChange={(e) => onFormChange("win_by_two", e.target.checked)}
                />
                Win By Two
              </label>

              <label className="text-sm inline-flex items-center gap-2 mt-6">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => onFormChange("is_active", e.target.checked)}
                />
                Active
              </label>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-4 mb-4">
            <div className="grid md:grid-cols-3 gap-3">
              <label className="text-sm md:col-span-2">
                Search
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Sport / Game / Game Type / Source"
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </label>
              <label className="text-sm">
                Status
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 border">#</th>
                    <th className="p-2 border">Sport</th>
                    <th className="p-2 border">Game</th>
                    <th className="p-2 border">Type</th>
                    <th className="p-2 border">Format</th>
                    <th className="p-2 border">Priority</th>
                    <th className="p-2 border">Status</th>
                    <th className="p-2 border">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td className="p-3 text-center text-gray-500 border" colSpan={8}>
                        Loading...
                      </td>
                    </tr>
                  ) : null}
                  {!loading && !visibleRules.length ? (
                    <tr>
                      <td className="p-3 text-center text-gray-500 border" colSpan={8}>
                        No rules found.
                      </td>
                    </tr>
                  ) : null}
                  {!loading &&
                    visibleRules.map((row, idx) => (
                      <tr key={row.id} className={!row.is_active ? "bg-gray-50" : ""}>
                        <td className="p-2 border">{idx + 1}</td>
                        <td className="p-2 border">{row.sport_name || (row.sport_id ? sportById[String(row.sport_id)] : "Any") || "Any"}</td>
                        <td className="p-2 border">{row.game_name || "Any"}</td>
                        <td className="p-2 border">{row.game_type || "Any"}</td>
                        <td className="p-2 border">
                          {`${row.segment_label} x ${row.sets_count}`}
                          {Number(row.points_per_set || 0) > 0 ? ` (${row.points_per_set})` : " (open)"}
                        </td>
                        <td className="p-2 border">{row.priority}</td>
                        <td className="p-2 border">{row.is_active ? "Active" : "Inactive"}</td>
                        <td className="p-2 border">
                          <div className="flex gap-2">
                            <button
                              onClick={() => editRule(row)}
                              className="px-2 py-1 rounded bg-amber-500 hover:bg-amber-600 text-white text-xs"
                            >
                              Edit
                            </button>
                            {row.is_active ? (
                              <button
                                onClick={() => deactivateRule(row)}
                                className="px-2 py-1 rounded bg-rose-600 hover:bg-rose-700 text-white text-xs"
                              >
                                Deactivate
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ScoreSheetRules;
